import {
  Text,
  Button,
  GreyGrey30,
  GreyGrey50,
  GreyGrey60,
  GreyGrey70,
  IconBuildings,
  Image,
  IndigoIndigo60,
  Link,
  Modal,
  Pill,
  PopoverWrapper,
  Primary13Regular,
  Primary13SemiBold,
  useModalState,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useJournalDetailTotals } from 'RevenueRecognition/view/revenueRecognitionDashboard/content/useJournalDetailTotals'
import { useJournalDetailSummary } from 'RevenueRecognition/view/revenueRecognitionDashboard/content/useJournalDetailSummary'
import { CustomerPreviewCard } from 'Customer/components/CustomerPreviewCard'
import { getLogoSrc } from 'Customer/utils/logo'
import { format } from '@sequencehq/utils/dates'
import { toMoney } from '@sequencehq/utils'
import { humanize } from 'inflection'
import { Currency } from '@sequencehq/api/commonEnums'
import PageError from 'components/ErrorPages/PageError'
import { ErrorBoundary } from 'common/components/ErrorBoundary/ErrorBoundary'
import { Summary } from 'RevenueRecognition/view/common/Summary'
import { ManualJournalModal } from './ManualJournalModal/ManualJournalModal'
import { ProtectedButton } from 'lib/rbac'

interface JournalDetailOverviewDrawerProps {
  journalId: string | null
  selectedCurrency: Currency | null
  closeDrawer: () => void
}

const JournalDetailOverviewDrawerContent = ({
  journalId,
  selectedCurrency,
  closeDrawer
}: {
  journalId: string
  selectedCurrency: Currency | null
  closeDrawer: () => void
}) => {
  const { isModalOpen, openModal, closeModal } = useModalState()
  const summaryData = useJournalDetailSummary({ journalId: journalId })
  const totalsData = useJournalDetailTotals({ journalId: journalId })

  const summary = summaryData.summary

  const summaryRows = [
    {
      title: 'Customer',
      value: summary?.customer ? (
        <PopoverWrapper
          popover={<CustomerPreviewCard customerId={summary.customer.id} />}
          offset={[-4, 4]}
          openDelay={850}
        >
          <Link.Unstyled
            to={`/customers/${summary.customer.id}`}
            data-testid="revrec.journalDetailOverview.summary.customer"
          >
            <Pill
              icon={
                summary.customer.domain ? (
                  <Image
                    height="16px"
                    width="16px"
                    src={getLogoSrc({
                      domain: summary.customer.domain
                    })}
                  />
                ) : (
                  <IconBuildings color={GreyGrey70} />
                )
              }
              interactive
              variant="ghost"
            >
              {summary.customer.name}
            </Pill>
          </Link.Unstyled>
        </PopoverWrapper>
      ) : (
        '-'
      )
    },
    {
      title: 'Invoice',
      value: summary?.invoice ? (
        <Link.Unstyled
          to={`/invoices/${summary.invoice.id}`}
          data-testid="revrec.journalDetailOverview.summary.invoice"
        >
          <Text
            {...Primary13Regular}
            color={IndigoIndigo60}
            fontWeight="semibold"
          >
            {summary.invoice.name}
          </Text>
        </Link.Unstyled>
      ) : (
        '-'
      )
    },
    ...(summary?.creditNote
      ? [
          {
            title: 'Credit note',
            value: (
              <Link.Unstyled
                to={`/credit-notes/${summary.creditNote.id}`}
                data-testid="revrec.journalDetailOverview.summary.creditNote"
              >
                <Text
                  {...Primary13Regular}
                  color={IndigoIndigo60}
                  fontWeight="semibold"
                >
                  {summary.creditNote.name}
                </Text>
              </Link.Unstyled>
            )
          }
        ]
      : []),
    {
      title: 'Product',
      value: summary?.product ? (
        <Link.Unstyled
          to={`/products/${summary.product.id}`}
          data-testid="revrec.journalDetailOverview.summary.product"
        >
          <Text
            {...Primary13Regular}
            color={IndigoIndigo60}
            fontWeight="semibold"
          >
            {summary.product.name}
          </Text>
        </Link.Unstyled>
      ) : (
        '-'
      )
    },
    {
      title: 'Type',
      value: (
        <Text
          {...Primary13Regular}
          fontWeight="semibold"
          data-testid="revrec.journalDetailOverview.summary.type"
        >
          {summary?.journalType === 'MANUAL'
            ? 'Adjustment journal'
            : 'Core journal'}
        </Text>
      )
    },
    {
      title: 'Entry date',
      value: summary?.date ? (
        <Text
          {...Primary13Regular}
          fontWeight="semibold"
          data-testid="revrec.journalDetailOverview.summary.date"
        >
          {format(summary.date, 'd MMM yyyy')}
        </Text>
      ) : (
        ''
      )
    },
    {
      title: 'Narrative',
      value: summary?.narrative ? (
        <Text
          {...Primary13Regular}
          textAlign="right"
          data-testid="revrec.journalDetailOverview.summary.narrative"
        >
          {summary.narrative}
        </Text>
      ) : (
        ''
      ),
      align: 'flex-start' as const,
      borderBottom: false
    }
  ]

  return (
    <>
      <Modal.Header showCloseButton withBorder>
        Overview
      </Modal.Header>
      <ErrorBoundary
        name="JournalDetailOverviewDrawer.content"
        fallback={PageError}
      >
        <Modal.Content>
          <Flex flexDirection="column" gap="24px">
            <Summary rows={summaryRows} />

            <Flex flexDirection="column">
              <Flex pt="4px" pb="8px">
                <Text {...Primary13SemiBold}>Breakdown</Text>
              </Flex>

              <Box mb="16px">
                <Flex borderBottom={`1px solid ${GreyGrey30}`} py="8px">
                  <Text
                    {...Primary13SemiBold}
                    fontWeight="semibold"
                    color={GreyGrey60}
                    flex="1"
                  >
                    Name
                  </Text>
                  <Text
                    {...Primary13SemiBold}
                    fontWeight="semibold"
                    color={GreyGrey60}
                    width="100px"
                    textAlign="right"
                  >
                    Debit
                  </Text>
                  <Text
                    {...Primary13SemiBold}
                    fontWeight="semibold"
                    color={GreyGrey60}
                    width="100px"
                    textAlign="right"
                  >
                    Credit
                  </Text>
                </Flex>
                <Flex flexDirection="column">
                  {totalsData.data?.entries?.map(entry => (
                    <Flex
                      key={entry.id}
                      py="8px"
                      sx={{
                        '&:not(:last-child)': {
                          borderBottom: `1px solid ${GreyGrey30}`
                        }
                      }}
                    >
                      <Text
                        {...Primary13Regular}
                        flex="1"
                        data-testid={`revrec.journalDetailOverview.totals.name-${entry.account.name}`}
                      >
                        {humanize(entry.account.name)}
                      </Text>
                      <Text
                        {...Primary13Regular}
                        width="100px"
                        textAlign="right"
                      >
                        {entry.direction === 'DEBIT'
                          ? toMoney({
                              currency: selectedCurrency as Currency,
                              value: entry.amount.toString()
                            })
                          : ''}
                      </Text>
                      <Text
                        {...Primary13Regular}
                        width="100px"
                        textAlign="right"
                      >
                        {entry.direction === 'CREDIT'
                          ? toMoney({
                              currency: selectedCurrency as Currency,
                              value: entry.amount.toString()
                            })
                          : ''}
                      </Text>
                    </Flex>
                  ))}
                </Flex>

                <Flex py="8px" borderTop={`1px solid ${GreyGrey50}`}>
                  <Text {...Primary13SemiBold} flex="1">
                    Total
                  </Text>
                  <Text
                    {...Primary13SemiBold}
                    width="100px"
                    textAlign="right"
                    data-testid="revrec.journalDetailOverview.totals.debit"
                  >
                    {toMoney({
                      currency: selectedCurrency as Currency,
                      value: (
                        totalsData.data?.totals.debit.amount || 0
                      ).toString()
                    })}
                  </Text>
                  <Text
                    {...Primary13SemiBold}
                    width="100px"
                    textAlign="right"
                    data-testid="revrec.journalDetailOverview.totals.credit"
                  >
                    {toMoney({
                      currency: selectedCurrency as Currency,
                      value: (
                        totalsData.data?.totals.credit.amount || 0
                      ).toString()
                    })}
                  </Text>
                </Flex>
              </Box>
            </Flex>
          </Flex>
        </Modal.Content>
      </ErrorBoundary>
      <Modal.Footer>
        <ProtectedButton permission="org.revenueRecognition:create">
          <Button
            data-testid="revrec.journalDetailOverview.createManualJournal.new"
            style={{
              width: '100%'
            }}
            onClick={openModal}
            variant="secondary"
          >
            Create adjustment journal
          </Button>
        </ProtectedButton>
        <ManualJournalModal
          journalId={journalId}
          isOpen={isModalOpen}
          onClose={closeModal}
          onSuccess={closeDrawer}
        />
      </Modal.Footer>
    </>
  )
}

/**
 * A controlled version of the JournalDetailDrawer that handles opening and closing the drawer
 * based on the journalId prop. When journalId changes, the drawer will automatically open.
 * When journalId is null, the drawer will not be rendered.
 */
export const JournalDetailOverviewDrawer = ({
  journalId,
  selectedCurrency,
  closeDrawer
}: JournalDetailOverviewDrawerProps) => {
  if (!journalId) {
    return null
  }

  return (
    <Modal
      isOpen
      onClose={closeDrawer}
      variant="drawer"
      data-testid="revrec.journalDetailOverview.drawer"
    >
      <ErrorBoundary name="JournalDetailOverviewDrawer" fallback={PageError}>
        <JournalDetailOverviewDrawerContent
          journalId={journalId}
          selectedCurrency={selectedCurrency}
          closeDrawer={closeDrawer}
        />
      </ErrorBoundary>
    </Modal>
  )
}
